import { erpApi } from "@/shared/axios";
import { urlBackend } from "@/shared/constants";
import type {
  FidelizacionConfig,
  FidelizacionContacto,
  FidelizacionEnvioPayload,
  FidelizacionLote,
  FidelizacionPage,
  FidelizacionProceso,
  FidelizacionSubirResponse,
} from "@/erp/campanas/fidelizacion-campana";

const BASE = `${urlBackend}api/v1/fidelizacion-campanas`;

const { get, post, remove } = erpApi();

///* mensaje legible de cualquier error del backend
export const getFidelizacionError = (error: unknown, fallback: string): string => {
  const err = error as {
    response?: { data?: { message?: string; detail?: string; error?: string } };
    message?: string;
  };
  return (
    err?.response?.data?.message ||
    err?.response?.data?.detail ||
    err?.response?.data?.error ||
    err?.message ||
    fallback
  );
};

///* capacidades del módulo: si hay api key, qué AppId y plantillas se pueden usar
export const getFidelizacionConfig = async (): Promise<FidelizacionConfig> => {
  const response = await get<FidelizacionConfig>(`${BASE}/config/`, true);
  return response as unknown as FidelizacionConfig;
};

///* Excel de ejemplo con el formato esperado (idContrato, cedula, parametro_1..N)
export const getFidelizacionPlantilla = async (): Promise<Blob> => {
  // typeJson=false: la respuesta es el Excel binario, no JSON. erpApi pone el
  // responseType blob y deja el Content-Type en manos de axios.
  const response = await get<Blob>(`${BASE}/plantilla/`, true, false);
  return response as unknown as Blob;
};

///* sube el Excel y crea el lote de contactos
export const subirFidelizacionArchivo = async (file: File): Promise<FidelizacionSubirResponse> => {
  const formData = new FormData();
  formData.append("file", file);
  // sin Content-Type: el navegador pone el boundary del multipart
  const response = await post<FidelizacionSubirResponse>(`${BASE}/subir/`, formData, true);
  return response as unknown as FidelizacionSubirResponse;
};

export interface FiltrosLotes {
  ///* yyyy-MM-dd; si vienen vacíos el backend no los aplica
  fechaDesde?: string;
  fechaHasta?: string;
}

export const getFidelizacionLotes = async (
  page = 0,
  size = 20,
  filtros: FiltrosLotes = {},
): Promise<FidelizacionPage<FidelizacionLote>> => {
  const params = new URLSearchParams({ page: String(page), size: String(size) });
  if (filtros.fechaDesde) params.set("fechaDesde", filtros.fechaDesde);
  if (filtros.fechaHasta) params.set("fechaHasta", filtros.fechaHasta);

  const response = await get<FidelizacionPage<FidelizacionLote>>(
    `${BASE}/lotes/?${params.toString()}`,
    true,
  );
  return response as unknown as FidelizacionPage<FidelizacionLote>;
};

export const getFidelizacionLote = async (
  loteId: number,
): Promise<FidelizacionLote> => {
  const response = await get<FidelizacionLote>(`${BASE}/lotes/${loteId}/`, true);
  return response as unknown as FidelizacionLote;
};

///* vista previa: contactos del lote, con filtro por estado y búsqueda libre
export const getFidelizacionContactos = async (
  loteId: number,
  page = 0,
  size = 50,
  estado?: string,
  busqueda?: string,
): Promise<FidelizacionPage<FidelizacionContacto>> => {
  const params = new URLSearchParams({ page: String(page), size: String(size) });
  if (estado) params.set("estado", estado);
  if (busqueda) params.set("busqueda", busqueda);

  const response = await get<FidelizacionPage<FidelizacionContacto>>(
    `${BASE}/lotes/${loteId}/contactos/?${params.toString()}`,
    true,
  );
  return response as unknown as FidelizacionPage<FidelizacionContacto>;
};

///* consulta nombre y teléfono de cada cédula (asíncrono en el microservicio)
export const enriquecerFidelizacion = async (loteId: number): Promise<FidelizacionProceso> => {
  const response = await post<FidelizacionProceso>(
    `${BASE}/lotes/${loteId}/enriquecer/`,
    {},
    true,
  );
  return response as unknown as FidelizacionProceso;
};

///* envía la plantilla HSM por WhatsApp a los contactos contactables (asíncrono)
///* ms-proveedores serializa en snake_case, así que el body va con ese naming:
///* mandar appId en camelCase lo haría ignorar en silencio y usaría el valor por defecto.
export const enviarFidelizacionHsm = async (
  loteId: number,
  payload: FidelizacionEnvioPayload,
): Promise<FidelizacionProceso> => {
  const body: Record<string, string> = {
    nombre_hsm: payload.nombreHsm,
  };
  ///* si no se indica, el backend usa el AppId configurado en SocialHub
  if (payload.appId) body.app_id = payload.appId;
  if (payload.campania) body.campania = payload.campania;
  if (payload.agente) body.agente = payload.agente;
  if (payload.parametros) body.parametros = payload.parametros;

  const response = await post<FidelizacionProceso>(
    `${BASE}/lotes/${loteId}/enviar/`,
    body,
    true,
  );
  return response as unknown as FidelizacionProceso;
};

export const cancelarFidelizacion = async (loteId: number): Promise<void> => {
  await post(`${BASE}/lotes/${loteId}/cancelar/`, {}, true);
};

///* progreso del enriquecimiento o del envío
export const getFidelizacionEstado = async (loteId: number): Promise<FidelizacionProceso> => {
  const response = await get<FidelizacionProceso>(`${BASE}/lotes/${loteId}/estado/`, true);
  return response as unknown as FidelizacionProceso;
};

export const eliminarFidelizacionLote = async (loteId: number): Promise<void> => {
  await remove(`${BASE}/lotes/${loteId}/`, true);
};
